/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button, Select, Tag, Space, Dropdown } from "antd";
import { DownloadOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import {
  DropDownComponent,
  TableComponent,
  RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { ReportsService } from "../../../../services/api/reports";
import { downloadHubAuditReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";
import API from "../../../../api";
import urls from "../../../../api/urls";
import { showErrorMessage } from "../../../../utils";
import moment from "moment";

const HubAuditReport = () => {
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [awbNumbers, setAwbNumbers] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  });
  const [reportData, setReportData] = useState([]);
  const [lastsynced, setLastsynced] = useState(null);
  const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
  // Update useEffect to use awbNumbers instead of destination hub
  useEffect(() => {
    if (awbNumbers.length > 0) {
      fetchReport();
    } else {
      // Clear table data when no AWB numbers
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0,
        current: 1
      }));
    }
    // eslint-disable-next-line
  }, [awbNumbers, selectedStatus, pagination.current, pagination.pageSize]);

  // Validate single AWB number
  const validateAwbNumber = (awb) => {
    if (!awb || awb.length === 0 || awb.length > 20) return false; // Max 20 characters

    const storedPattern = localStorage.getItem("regexPattern");
    if (storedPattern) {
      const cleanPattern = storedPattern?.replace(/^\/|\/$/g, "");
      const regex = new RegExp(cleanPattern);
      return regex.test(awb);
    }
    return true; // If no pattern, allow the AWB (but max 20 chars)
  };

  // Handle AWB input change
  const handleAwbChange = (values) => {
    // Filter out empty values and duplicates while preserving order
    const cleanedValues = values
      .map(awb => awb.trim().substring(0, 20)) // Limit to 20 characters
      .filter((awb, index, arr) => awb.length > 0 && arr.indexOf(awb) === index);

    // Validate and filter out invalid AWB numbers
    const validAwbs = [];
    const invalidAwbs = [];

    cleanedValues.forEach(awb => {
      if (validateAwbNumber(awb)) {
        validAwbs.push(awb);
      } else {
        invalidAwbs.push(awb);
      }
    });

    // Show error message for invalid AWBs but don't add them
    if (invalidAwbs.length > 0) {
      showErrorMessage(`Invalid AWB number format: ${invalidAwbs.join(", ")}`);
    }

    // Only set valid AWB numbers
    setAwbNumbers(validAwbs);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // Remove individual AWB number
  const removeAwbNumber = (awbToRemove) => {
    const updatedAwbs = awbNumbers
      .filter(awb => awb !== awbToRemove)
      .filter(awb => validateAwbNumber(awb)); // Extra safety: only keep valid AWBs
    setAwbNumbers(updatedAwbs);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      setReportData([]);
      const params = {
        ...(awbNumbers.length > 0 && { awbNumbers: awbNumbers.join(",") }),
        ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
        page: (pagination.current - 1), // Convert to 0-based
        size: pagination.pageSize
      };

      console.log("Audut Report:", params);

      // Call the Incoming LCR report API
      const { data, total, page, pageSize, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getHubAuditReport(params);
      setReportData(data);
      setPagination(prev => ({
        ...prev,
        total,
      }));
      setLastsynced(lastRefreshedAt);
      setNextScheduledRefresh(nextScheduledRefresh);
    } catch {
      showErrorMessage("Failed to fetch report data");
      setReportData([]);
      setPagination(prev => ({
        ...prev,
        total: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
  };

  const handleDownload = async () => {
    // Check if AWB numbers field is empty
    if (awbNumbers.length === 0) {
      showErrorMessage("Please enter at least one AWB number before downloading the report");
      return;
    }

    setDownloadLoading(true);
    try {
      const downloadParams = {
        ...(awbNumbers.length > 0 && { awbNumbers: awbNumbers.join(",") }),
        ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") })
      };

      // console.log("downloadParams (Outgoing LCRfrom click):", downloadParams);

      await downloadHubAuditReport(downloadParams);
      message.success("Report downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download report");
    } finally {
      setDownloadLoading(false);
    }
  };

  const navigate = useNavigate();

  // Handler for cell clicks (fetch details and navigate)
  const handleCellClick = async (type, record) => {
    let status = type;
    // Map shipment types to required API status
    if (type === "shipmentOut") status = "SHIPMENT_OUT";
    else if (type === "shipmentIn") status = "SHIPMENT_IN";
    else if (type === "shipmentPending") status = "SHIPMENT_PENDING";
    // Map bag types to required API status
    else if (type === "bagOut") status = "BAG_OUT";
    else if (type === "bagIn") status = "BAG_IN";
    else if (type === "bagPending") status = "BAG_PENDING";
    let detailType = ["bagOut", "bagIn", "bagPending"].includes(type) ? "Bag" : "Shipment";
    let endpoint = detailType === "Bag" ? urls.BAG_DETAIL_VIEW_LCR : urls.SHIPMENT_DETAIL_VIEW_LCR;
    let lcrNum = record.stopLcrNum;
    let vehicleNum = record.vehicleNum || record.vehicle_num;
    let currentHub = record.fromHub || record.current_hub;
    try {
      const queryParams = {
        lcrNum,
        type: status,
      };
      const apiResult = await API.Api(endpoint).getBagDetailsParams(queryParams);
      let dataArr = apiResult.response.data.data;
      if (apiResult.response.data.code == 200) {
        if (apiResult.response.data.data.length > 0) {
          navigate("/detailed-inout-view", {
            state: {
              vehicle_lcr_id: lcrNum,
              vehicle_num: vehicleNum,
              current_hub: currentHub,
              type: detailType,
              status,
              data: dataArr,
              pageFrom: "OutGoingLCReport"
            },
          });
        } else {
          showErrorMessage(apiResult.response.data.message);
        }
      } else {
        showErrorMessage("Something went wrong");
      }
    } catch (error) {
      message.error("An error occurred while fetching details");
    }
  };

  return (
    <div className="report-container">
      <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
        <Heading title="Hub Audit Report">
          <div style={{ paddingTop: "5px" }}>
            <RefreshTimestamp 
                lastRefreshedAt={lastsynced}
                nextScheduledRefresh={nextScheduledRefresh}
            />
          </div>
        </Heading>
      </div>
      <Card className="report-card">
        <Row gutter={[16, 16]} className="filters-section">
          <Col xs={24} sm={12} md={8} lg={6}>
            <div className="filter-label">AWB Numbers</div>
            <Dropdown
              overlay={
                <div style={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  padding: "8px",
                  backgroundColor: "white",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  boxShadow: "0 6px 16px 0 rgba(0, 0, 0, 0.08)",
                  minWidth: "250px"
                }}>
                  <div style={{ marginBottom: "8px", fontWeight: "500" }}>
                    Valid AWB Numbers ({awbNumbers.filter(awb => validateAwbNumber(awb)).length})
                  </div>
                  <Space size={[4, 4]} wrap>
                    {awbNumbers
                      .filter(awb => validateAwbNumber(awb)) // Only show valid AWB numbers
                      .map((awb) => (
                        <Tag
                          key={awb}
                          closable
                          onClose={() => removeAwbNumber(awb)}
                          style={{ margin: "2px" }}
                        >
                          {awb}
                        </Tag>
                      ))}
                  </Space>
                  {awbNumbers.length > 0 && (
                    <div style={{ marginTop: "8px", textAlign: "right" }}>
                      <Button
                        size="small"
                        danger
                        onClick={() => {
                          // Only clear valid AWB numbers (extra safety)
                          const validAwbs = awbNumbers.filter(awb => validateAwbNumber(awb));
                          if (validAwbs.length > 0) {
                            setAwbNumbers([]);
                            setPagination(prev => ({ ...prev, current: 1 }));
                          }
                          setDropdownVisible(false);
                        }}
                      >
                        Clear All
                      </Button>
                    </div>
                  )}
                </div>
              }
              trigger={["click"]}
              placement="bottomLeft"
              visible={dropdownVisible && awbNumbers.filter(awb => validateAwbNumber(awb)).length > 2}
              onVisibleChange={(visible) => {
                if (awbNumbers.filter(awb => validateAwbNumber(awb)).length > 2) {
                  setDropdownVisible(visible);
                }
              }}
            >
              <div onClick={() => {
                if (awbNumbers.filter(awb => validateAwbNumber(awb)).length > 2) {
                  setDropdownVisible(!dropdownVisible);
                }
              }}>
                <Select
                  mode="tags"
                  value={awbNumbers}
                  onChange={handleAwbChange}
                  onInputKeyDown={(e) => {
                    // Get current input value
                    const inputElement = e.target;
                    const currentValue = inputElement.value || "";

                    // Prevent typing if current value is 20 characters and user is trying to add more
                    if (currentValue.length >= 20 &&
                      !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
                        "Tab", "Enter", " ", ","].includes(e.key)) {
                      e.preventDefault();
                      return;
                    }

                    // Prevent forbidden characters
                    const forbiddenChars = /[@!$#%^&*=+()""';?.><]/;
                    if (forbiddenChars.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Enter AWB numbers and press Enter, comma, or space"
                  style={{
                    width: "100%",
                    minHeight: "32px"
                  }}
                  allowClear
                  tokenSeparators={[",", " ", "\n"]}
                  maxTagCount="responsive"
                  dropdownStyle={{ display: "none" }}
                  notFoundContent={null}

                  onClick={(e) => {
                    if (awbNumbers.filter(awb => validateAwbNumber(awb)).length > 2) {
                      e.preventDefault();
                      setDropdownVisible(!dropdownVisible);
                    }
                  }}
                />
              </div>
            </Dropdown>
            <div style={{ fontSize: "12px", color: "#8c8c8c", marginTop: "4px" }}>
              Enter AWB numbers and press Enter, comma, or space to add. Click × to remove. Max 20 characters per AWB.
            </div>
          </Col>
          {/* <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Status</div>
                        <DropDownComponent
                            placeholder="Select Status"
                            options={statusList}
                            onChange={(value) => {
                                setSelectedStatus(value);
                                setPagination(prev => ({ ...prev, current: 1 }));
                            }}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                        />
                    </Col>*/}
        </Row>
        <Row style={{ marginTop: 16 }}>
          <Col span={24} style={{ textAlign: "right" }}>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={downloadLoading}
              disabled={awbNumbers.length === 0}
              onClick={handleDownload}
            >
              Download Report
            </Button>
          </Col>
        </Row>
      </Card>
      <TableComponent
        columns={[
          { title: "Booking Date", dataIndex: "bookingDate", key: "bookingDate", sorter: true, align: "center", },
          { title: "Awb Number", dataIndex: "documentNo", key: "documentNo", sorter: true, align: "center", },
          { title: "Sender", dataIndex: "sender", key: "sender", sorter: true, align: "center", },
          { title: "Receiver", dataIndex: "receiver", key: "receiver", sorter: true, align: "center", },
          { title: "From Center", dataIndex: "fromCenter", key: "fromCenter", sorter: true, align: "center", },
          { title: "To Center", dataIndex: "toCenter", key: "toCenter", sorter: true, align: "center", },
          { title: "Invoice No", dataIndex: "invoiceNo", key: "invoiceNo", sorter: true, align: "center", },
          { title: "Service Type", dataIndex: "serviceType", key: "serviceType", sorter: true, align: "center", },
          { title: "Document Type", dataIndex: "documentType", key: "documentType", sorter: true, align: "center", },
          { title: "Travel By", dataIndex: "travelBy", key: "travelBy", sorter: true, align: "center", },
          { title: "Booking Weight (kg)", dataIndex: "bookingWeight", key: "bookingWeight", sorter: true, align: "center", },
          { title: "Length", dataIndex: "length", key: "length", sorter: true, align: "center", },
          { title: "Width", dataIndex: "width", key: "width", sorter: true, align: "center", },
          { title: "Height", dataIndex: "height", key: "height", sorter: true, align: "center", },
          { title: "Volumetric Weight (kg)", dataIndex: "volumetricWeight", key: "volumetricWeight", sorter: true, align: "center", },
          { title: "Charges", dataIndex: "charges", key: "charges", sorter: true, align: "center", },
          
        ]}
        dataSource={reportData}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey="documentNo"
        style={{ marginTop: 32 }}
      />


    </div>
  );
};

export default HubAuditReport;
